// Lib
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { useSelector } from 'react-redux';

// Store
import { groupsStore } from './groupsStore';

// Types
import type { AuthGroup } from '@app-types/index';

/* Reads the shared groups store (one fetch per session) instead of the old
 * per-instance no-cache fetch that refired AuthGroupList on every mount. The
 * returned shapes are unchanged, so the operation hooks keep working — their
 * `setGroups` now writes to the shared store, updating every consumer live. */
export const useGroupsList = () => {
  const organizationId = useSelector(
    (state: { SelectedOrganization?: { _id?: string } | null }) =>
      state.SelectedOrganization?._id ?? null,
  );
  const groups = useSyncExternalStore(
    groupsStore.subscribe,
    groupsStore.getGroups,
  );
  const isLoadingList = useSyncExternalStore(
    groupsStore.subscribe,
    groupsStore.getLoading,
  );
  const [isSaveToGroup, setIsSaveToGroup] = useState<boolean>(false);

  // Master-group ops gate on this ref ("don't save until the list has loaded").
  const loadingRef = useRef<boolean>(isLoadingList);
  loadingRef.current = isLoadingList;

  // Reuse the shared snapshot inside one organization. An organization switch
  // clears it and starts a newly scoped request before consumers can use an old
  // master/list ID.
  useEffect(() => {
    void groupsStore.ensureLoaded(organizationId);
  }, [organizationId]);

  const setGroups = useCallback(
    (updater: React.SetStateAction<AuthGroup[]>): void => {
      groupsStore.setGroups(updater);
    },
    [],
  );

  return {
    groups,
    setGroups,
    isSaveToGroup,
    setIsSaveToGroup,
    isLoadingList,
    loadingRef,
  };
};
